import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NotificationToastComponent } from '../../tools/notification-toast/notification-toast.component';
import { Notification, NotificationInput, NotificationPatch } from '../model/types';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {

  readonly maxNotifications = 50;
  readonly maxAgeDays = 30;

  private readonly snackBar = inject(MatSnackBar);
  private readonly authService = inject(AuthService);

  private readonly _notifications = signal<Notification[]>([]);

  constructor() {
    this.loadFromStorage();
    this.setupStoragePersistence();
    this.setupSessionCleanup();
  }

  private prune(notifications: Notification[]): Notification[] {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - this.maxAgeDays);

    let pruned = notifications.filter(n => !n.isRead || new Date(n.timestamp) >= cutoff);

    if (pruned.length > this.maxNotifications) {
      pruned = pruned
        .slice()
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        .slice(0, this.maxNotifications);
    }

    return pruned;
  }

  private loadFromStorage(): void {
    try {
      const key = this.storageKey();
      if (!key) return;
      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as Notification[];
        const pruned = this.prune(parsed);
        this._notifications.set(pruned);
      }
    } catch {
      // localStorage invalide ou corrompu → on repart à zéro
    }
  }

  private setupStoragePersistence(): void {
    effect(() => {
      const key = this.storageKey();
      if (!key) return;
      localStorage.setItem(key, JSON.stringify(this._notifications()));
    });
  }

  private setupSessionCleanup(): void {
    effect(() => {
      if (!this.authService.isLoggedIn()) {
        this.snackBar.dismiss();
      }
    });
  }

  private storageKey(): string | null {
    const user = this.authService.getLoggedUser();
    return user?.Id ? `notifications_${user.Id}` : null;
  }

  private showToast(icon: string, labelKey: string, labelParams?: Record<string, unknown>): void {
    if (!this.authService.isLoggedIn()) return;
    this.snackBar.openFromComponent(NotificationToastComponent, {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      duration: 4000,
      panelClass: 'notification-toast-panel',
      data: { icon, labelKey, labelParams },
    });
  }

  readonly notifications = computed(() =>
    [...this._notifications()].sort((a, b) => b.timestamp.localeCompare(a.timestamp))
  );

  readonly unreadCount = computed(() => this._notifications().filter(n => !n.isRead).length);

  markAllAsRead(): void {
    this._notifications.update(prev => prev.map(n => ({ ...n, isRead: true })));
  }

  add(notification: NotificationInput, options?: { silent?: boolean }): string {
    const id = crypto.randomUUID();
    this._notifications.update(prev => this.prune([{
      ...notification,
      id,
      timestamp: new Date().toISOString(),
      isRead: false,
    } as Notification, ...prev]));

    if (!options?.silent) {
      this.showToast(notification.icon, notification.labelKey, notification.labelParams);
    }

    return id;
  }

  update(id: string, partial: NotificationPatch, options?: { silent?: boolean }): void {
    this._notifications.update(prev =>
      prev.map(n => n.id === id ? { ...n, ...partial, timestamp: new Date().toISOString() } as Notification : n)
    );

    if (!options?.silent) {
      const updated = this._notifications().find(n => n.id === id);
      if (updated) {
        this.showToast(updated.icon, updated.labelKey, updated.labelParams);
      }
    }
  }

  remove(id: string): void {
    this._notifications.update(prev => prev.filter(n => n.id !== id));
  }
}
